<script setup>
defineOptions({
  name: 'ToastComponent',
});

import { ref, onMounted, watch } from 'vue';
import { Link } from '@inertiajs/vue3';
import { CheckCircle, AlertCircle, XCircle, Info, X } from '@lucide/vue';

const props = defineProps({
  id: {
    type: String,
    required: true
  },
  type: {
    type: String,
    default: 'success',
    validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
  },
  title: {
    type: String,
    default: ''
  },
  message: {
    type: String,
    required: true
  },
  duration: {
    type: Number,
    default: 9000
  },
  persistent: {
    type: Boolean,
    default: false
  },
  // Optional follow-up link under the message, e.g. { url, label } to
  // Mijn exports. Rendered as an Inertia Link — never as HTML inside the
  // message, so a message can't inject markup.
  link: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['remove']);

const isVisible = ref(false);
const isRemoving = ref(false);

// Auto-remove timer
let removeTimer = null;

// Show toast with animation
onMounted(() => {
  setTimeout(() => {
    isVisible.value = true;
  }, 50);

  // Start auto-remove timer if not persistent
  if (!props.persistent && props.duration > 0) {
    removeTimer = setTimeout(() => {
      handleRemove();
    }, props.duration);
  }
});

// Handle manual removal
function handleRemove() {
  if (removeTimer) {
    clearTimeout(removeTimer);
  }
  
  isRemoving.value = true;
  
  // Wait for exit animation to complete
  setTimeout(() => {
    emit('remove', props.id);
  }, 300);
}

// Clean up timer on unmount
function cleanup() {
  if (removeTimer) {
    clearTimeout(removeTimer);
  }
}

// Get icon based on type
function getIcon() {
  switch (props.type) {
    case 'success':
      return CheckCircle;
    case 'error':
      return XCircle;
    case 'warning':
      return AlertCircle;
    case 'info':
      return Info;
    default:
      return CheckCircle;
  }
}

// Get styling classes based on type
function getTypeClasses() {
  switch (props.type) {
    case 'success':
      return {
        container: 'bg-green-50 border-green-200',
        icon: 'text-green-500',
        title: 'text-green-800',
        message: 'text-green-700',
        closeButton: 'text-green-400 hover:text-green-600'
      };
    case 'error':
      return {
        container: 'bg-red-50 border-red-200',
        icon: 'text-red-500',
        title: 'text-red-800',
        message: 'text-red-700',
        closeButton: 'text-red-400 hover:text-red-600'
      };
    case 'warning':
      return {
        container: 'bg-orange-50 border-orange-200',
        icon: 'text-orange-500',
        title: 'text-orange-800',
        message: 'text-orange-700',
        closeButton: 'text-orange-400 hover:text-orange-600'
      };
    case 'info':
      return {
        container: 'bg-blue-50 border-blue-200',
        icon: 'text-blue-500',
        title: 'text-blue-800',
        message: 'text-blue-700',
        closeButton: 'text-blue-400 hover:text-blue-600'
      };
    default:
      return {
        container: 'bg-green-50 border-green-200',
        icon: 'text-green-500',
        title: 'text-green-800',
        message: 'text-green-700',
        closeButton: 'text-green-400 hover:text-green-600'
      };
  }
}

// Cleanup on component unmount
watch(() => {}, cleanup);
</script>

<template>
  <div
    :class="[
      'pointer-events-auto w-full max-w-md sm:max-w-lg rounded-lg border shadow-lg transition-all duration-300 ease-in-out',
      getTypeClasses().container,
      {
        'transform translate-x-0 opacity-100': isVisible && !isRemoving,
        'transform translate-x-full opacity-0': !isVisible || isRemoving
      }
    ]"
  >
    <div class="p-4">
      <div class="flex items-start">
        <!-- Icon -->
        <div class="flex-shrink-0">
          <component 
            :is="getIcon()" 
            :class="['h-5 w-5', getTypeClasses().icon]"
          />
        </div>
        
        <!-- Content -->
        <div class="ml-3 flex-1 min-w-0">
          <!-- Title (if provided) -->
          <p 
            v-if="title" 
            :class="['text-sm font-medium', getTypeClasses().title]"
          >
            {{ title }}
          </p>
          
          <!-- Message -->
          <p 
            :class="[
              'text-sm leading-relaxed break-words',
              getTypeClasses().message,
              { 'mt-1': title }
            ]"
          >
            {{ message }}
          </p>

          <!-- Link (if provided) -->
          <Link
            v-if="link"
            :href="link.url"
            :class="['mt-1 inline-block text-sm font-medium underline', getTypeClasses().title]"
          >
            {{ link.label }}
          </Link>
        </div>
        
        <!-- Close button -->
        <div class="ml-4 flex flex-shrink-0">
          <button
            @click="handleRemove"
            :class="[
              'inline-flex rounded-md p-1.5 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2',
              getTypeClasses().closeButton
            ]"
          >
            <span class="sr-only">Sluiten</span>
            <X class="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template> 